import { useState, useEffect } from "react";
import axiosInstance from "../../axiosInstace";
import { useWorkspace, WORKSPACES } from "../../contexts/Workspacecontext";
import { useAuth } from "../../contexts/AuthContext";
import { RECRUITMENT, CONSULTANCY } from "../../config/moduleAccess";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import HiringPipelineCard from "./Hiringpipelinecard";
import MiniCalendar from "./MiniCalender";

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  blue: "#2563eb",
  blueLt: "#eff6ff",
  teal: "#0f6e56",
  tealLt: "#e1f5ee",
  amber: "#d97706",
  amberLt: "#fef3c7",
  violet: "#6d28d9",
  violetLt: "#f5f3ff",
  red: "#dc2626",
  redLt: "#fee2e2",
  green: "#15803d",
  greenLt: "#dcfce7",
  border: "#e5e7eb",
  text1: "#111827",
  text2: "#374151",
  text3: "#6b7280",
  text4: "#9ca3af",
  bg: "#f3f4f6",
  surface: "#ffffff",
  surface2: "#f9fafb",
};

const font = "'DM Sans', system-ui, sans-serif";
const mono = "'DM Mono', 'Courier New', monospace";
const P = 10; // universal padding

// ─── API endpoints ────────────────────────────────────────────────────────────
const APIS = {
  hrDashboard: `${process.env.REACT_APP_API_EMPLOYEE_URL}/common/dashboard`,
  hrSlots: `${process.env.REACT_APP_API_EMPLOYEE_URL}/slots/common/dashboard`,
  empDashboard: `${process.env.REACT_APP_API_EMPLOYEE_URL}/common/dashboard`,
  empSlots: `${process.env.REACT_APP_API_EMPLOYEE_URL}/slots/common/dashboard`,
  mySlots: `${process.env.REACT_APP_API_EMPLOYEE_URL}/slots/my-slots/dashboard`,
  hrConsultancy: `${process.env.REACT_APP_API_EMPLOYEE_URL}/consultancy/dashboard`,
  consultancy: `${process.env.REACT_APP_API_EMPLOYEE_URL}/consultancy/dashboard`,
  empConsultancy: `${process.env.REACT_APP_API_EMPLOYEE_URL}/consultancy/dashboard`,
};

// ─── Applied via config ───────────────────────────────────────────────────────
const APPLIED_VIA_CFG = {
  linkedlin: { label: "LinkedIn", color: C.blue },
  jobdrive: { label: "Job drive", color: C.violet },
  nakuri: { label: "Naukri", color: C.amber },
  consultancy: { label: "Consultancy", color: C.teal },
  external: { label: "External", color: C.amber },
  self: { label: "Self", color: "#0891b2" },
  others: { label: "Others", color: C.text4 },
  link: { label: "Link", color: "#db2777" },
  jobdriveOrWalkin: { label: "Walk-in", color: C.teal },
  internal: { label: "Internal", color: C.violet },
};

const parseAppliedVia = (av = {}) => {
  const total = Object.values(av).reduce((s, v) => s + (v || 0), 0) || 1;
  return Object.entries(av)
    .filter(([, v]) => v > 0)
    .sort(([, a], [, b]) => b - a)
    .map(([key, count]) => ({
      label: APPLIED_VIA_CFG[key]?.label || key,
      color: APPLIED_VIA_CFG[key]?.color || C.text4,
      count,
      pct: Math.round((count / total) * 100),
    }));
};

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CFG = {
  open: { color: C.green, bg: C.greenLt, label: "Open" },
  active: { color: C.green, bg: C.greenLt, label: "Active" },
  draft: { color: C.text3, bg: C.surface2, label: "Draft" },
  closed: { color: C.red, bg: C.redLt, label: "Closed" },
  cancelled: { color: C.red, bg: C.redLt, label: "Cancelled" },
  inprogress: { color: C.blue, bg: C.blueLt, label: "In progress" },
  in_progress: { color: C.blue, bg: C.blueLt, label: "In progress" },
  hold: { color: C.amber, bg: C.amberLt, label: "On hold" },
  on_hold: { color: C.amber, bg: C.amberLt, label: "On hold" },
  selected: { color: C.violet, bg: C.violetLt, label: "Selected" },
  approved: { color: C.green, bg: C.greenLt, label: "Approved" },
  pending: { color: C.amber, bg: C.amberLt, label: "Pending" },
  rejected: { color: C.red, bg: C.redLt, label: "Rejected" },
};
const getStatus = (s) => {
  if (!s) return { color: C.text3, bg: C.surface2, label: "Unknown" };
  const key = s.toLowerCase().replace(/[-\s]/g, "_");
  return STATUS_CFG[key] || { color: C.text3, bg: C.surface2, label: s };
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const MN = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const pad = (n) => String(n).padStart(2, "0");
const fmtTime = (s) => {
  const d = new Date(s);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const SLOT_META = {
  booked: { color: C.blue, bg: C.blueLt, label: "Confirmed" },
  available: { color: C.green, bg: C.greenLt, label: "Open" },
  blocked: { color: C.red, bg: C.redLt, label: "Blocked" },
  rescheduled: { color: C.amber, bg: C.amberLt, label: "Rescheduled" },
  inactive: { color: C.text3, bg: C.surface2, label: "Inactive" },
};

// ══════════════════════════════════════════════════════════════════════════════
// BASE ATOMS
// ══════════════════════════════════════════════════════════════════════════════
function FontLoader() {
  useEffect(() => {
    if (document.querySelector("#dash-font")) return;
    const l = document.createElement("link");
    l.id = "dash-font";
    l.href =
      "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&family=DM+Mono:wght@400;500&display=swap";
    l.rel = "stylesheet";
    document.head.appendChild(l);
  }, []);
  return null;
}

// Uniform card — always 10px padding on all sides
const Card = ({ children, style }) => (
  <div
    style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 10,
      padding: P,
      fontFamily: font,
      ...style,
    }}
  >
    {children}
  </div>
);

// Section label with optional right-side accessory
const SectionLabel = ({ children, count, right }) => (
  <div
    style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}
  >
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: C.text3,
        letterSpacing: "0.05em",
      }}
    >
      {children}
    </span>
    {count != null && (
      <span
        style={{
          background: C.blueLt,
          color: C.blue,
          fontSize: 10,
          fontWeight: 700,
          padding: "1px 6px",
          borderRadius: 20,
        }}
      >
        {count}
      </span>
    )}
    {right && (
      <span style={{ marginLeft: "auto", fontSize: 11, color: C.text4 }}>
        {right}
      </span>
    )}
  </div>
);

// Divider
const Div = () => (
  <div style={{ height: 1, background: C.border, margin: "8px 0" }} />
);

// ─── KPI stat card ────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, accent = C.blue, delay = 0 }) => (
  <div
    style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 10,
      padding: P,
      display: "flex",
      alignItems: "center",
      gap: 10,
      position: "relative",
      overflow: "hidden",
      fontFamily: font,
      animation: "dashFadeUp 0.4s ease both",
      animationDelay: `${delay}ms`,
    }}
  >
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: 3,
        height: "100%",
        background: accent,
        borderRadius: "10px 0 0 10px",
      }}
    />
    <div style={{ flex: 1, minWidth: 0, paddingLeft: 6 }}>
      <p
        style={{
          fontSize: 12,
          color: C.text2,
          margin: "0 0 2px",
          fontWeight: 600,
          lineHeight: 1.2,
        }}
      >
        {label}
      </p>
      {sub && <p style={{ fontSize: 10, color: C.text4, margin: 0 }}>{sub}</p>}
    </div>
    <span
      style={{
        fontSize: 28,
        fontWeight: 700,
        color: C.text1,
        fontFamily: mono,
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      {value ?? 0}
    </span>
  </div>
);

// ─── Badge ────────────────────────────────────────────────────────────────────
const Badge = ({ status }) => {
  const cfg = getStatus(status);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        background: cfg.bg,
        color: cfg.color,
        borderRadius: 20,
        padding: "2px 8px",
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          background: cfg.color,
          display: "inline-block",
        }}
      />
      {cfg.label}
    </span>
  );
};

// ─── Uniform data grid ────────────────────────────────────────────────────────
const DataGrid = ({ label, items, cols = 3 }) => {
  const colCount = Math.min(cols, items.length);
  return (
    <div>
      {label && (
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: C.text4,
            letterSpacing: "0.06em",
            display: "block",
            marginBottom: 4,
          }}
        >
          {label}
        </span>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${colCount}, 1fr)`,
          gap: 1,
          background: C.border,
          borderRadius: 7,
          overflow: "hidden",
        }}
      >
        {items.map((item) => (
          <div
            key={item.label}
            style={{
              background: C.surface,
              padding: "7px 10px",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <span style={{ fontSize: 10, color: C.text3 }}>{item.label}</span>
            <span
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: item.color || C.text1,
                fontFamily: mono,
                lineHeight: 1,
              }}
            >
              {item.value ?? 0}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Candidate flow strip ─────────────────────────────────────────────────────
const CandidateStrip = ({ candidates }) => {
  const items = [
    { label: "Applied", key: "totalApplied", color: "#d85a30", bg: "#fff3f0" },
    { label: "Scheduled", key: "scheduled", color: C.violet, bg: C.violetLt },
    { label: "Processing", key: "processing", color: C.blue, bg: C.blueLt },
    { label: "On hold", key: "hold", color: C.amber, bg: C.amberLt },
    { label: "Selected", key: "selected", color: C.green, bg: C.greenLt },
    { label: "Rejected", key: "rejected", color: C.red, bg: C.redLt },
  ];
  return (
    <div
      style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 6 }}
    >
      {items.map((i) => (
        <div
          key={i.key}
          style={{
            background: i.bg,
            borderRadius: 8,
            padding: P,
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: 24,
              fontWeight: 700,
              color: i.color,
              fontFamily: mono,
              lineHeight: 1,
            }}
          >
            {candidates[i.key] ?? 0}
          </div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: i.color,
              opacity: 0.8,
              marginTop: 3,
            }}
          >
            {i.label}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Bar row ──────────────────────────────────────────────────────────────────
const BarRow = ({ label, value, max, color }) => {
  const pct = max ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ marginBottom: 7 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 3,
        }}
      >
        <span style={{ fontSize: 11, color: C.text2 }}>{label}</span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: C.text1,
            fontFamily: mono,
          }}
        >
          {value}
        </span>
      </div>
      <div style={{ background: C.bg, borderRadius: 99, height: 5 }}>
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: color,
            borderRadius: 99,
            transition: "width .5s ease",
          }}
        />
      </div>
    </div>
  );
};

// ─── Donut chart ──────────────────────────────────────────────────────────────
const DonutChart = ({ data, centerLabel, centerValue }) => {
  const total = data.reduce((s, d) => s + (d.count || 0), 0) || 1;
  const R = 56,
    r = 35,
    cx = 70,
    cy = 70;
  let cum = 0;
  const slices = data
    .filter((d) => d.count > 0)
    .map((d) => {
      const a0 = (cum / total) * 2 * Math.PI - Math.PI / 2;
      cum += d.count;
      const a1 = (cum / total) * 2 * Math.PI - Math.PI / 2;
      const lg = d.count / total > 0.5 ? 1 : 0;
      const pt = (rr, a) => [cx + rr * Math.cos(a), cy + rr * Math.sin(a)];
      const [ox, oy] = pt(r, a0),
        [ix, iy] = pt(R, a0);
      const [ox2, oy2] = pt(r, a1),
        [ix2, iy2] = pt(R, a1);
      return {
        ...d,
        path: `M${ox},${oy} L${ix},${iy} A${R},${R} 0 ${lg} 1 ${ix2},${iy2} L${ox2},${oy2} A${r},${r} 0 ${lg} 0 ${ox},${oy} Z`,
      };
    });

  if (!data.length || !total)
    return <p style={{ fontSize: 12, color: C.text4 }}>No data.</p>;

  return (
    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
      <svg
        width={140}
        height={140}
        viewBox="0 0 140 140"
        style={{ flexShrink: 0 }}
      >
        {slices.map((s, i) => (
          <path
            key={i}
            d={s.path}
            fill={s.color}
            stroke={C.surface}
            strokeWidth={2}
          />
        ))}
        <text
          x={cx}
          y={cy - 8}
          textAnchor="middle"
          fontSize={10}
          fill={C.text4}
          fontFamily={font}
          fontWeight={500}
        >
          {centerLabel}
        </text>
        <text
          x={cx}
          y={cx + 10}
          textAnchor="middle"
          fontSize={20}
          fill={C.text1}
          fontFamily={mono}
          fontWeight={700}
        >
          {centerValue}
        </text>
      </svg>
      <div
        style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}
      >
        {data.map((d) => (
          <div
            key={d.label}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: 2,
                background: d.color,
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: 11, color: C.text2, flex: 1 }}>
              {d.label}
            </span>
            <span
              style={{
                fontSize: 11,
                color: C.text4,
                fontFamily: mono,
                marginRight: 4,
              }}
            >
              {d.count}
            </span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: C.text1,
                fontFamily: mono,
              }}
            >
              {d.pct}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Upcoming interviews ──────────────────────────────────────────────────────
const UpcomingInterviews = ({ slots = [] }) => {
  const list = [...slots]
    .filter((s) => ["booked", "rescheduled", "available"].includes(s.status))
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
    .slice(0, 5);
  if (!list.length)
    return (
      <p style={{ fontSize: 11, color: C.text4 }}>No upcoming interviews.</p>
    );
  return (
    <div>
      {list.map((s) => {
        const m = SLOT_META[s.status] || SLOT_META.inactive;
        const d = new Date(s.startTime);
        return (
          <div
            key={s.slotId}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 0",
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            <div style={{ textAlign: "center", minWidth: 30, flexShrink: 0 }}>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: C.text1,
                  lineHeight: 1,
                  fontFamily: mono,
                }}
              >
                {d.getDate()}
              </div>
              <div style={{ fontSize: 9, color: C.text4 }}>
                {MN[d.getMonth()]}
              </div>
            </div>
            <div
              style={{
                width: 1,
                background: C.border,
                alignSelf: "stretch",
                flexShrink: 0,
              }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.text1 }}>
                Job #{s.jobPostId}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginTop: 2,
                }}
              >
                <span style={{ fontSize: 11, color: C.text3 }}>
                  {fmtTime(s.startTime)} – {fmtTime(s.endTime)}
                </span>
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: m.color,
                    background: m.bg,
                    borderRadius: 4,
                    padding: "1px 5px",
                  }}
                >
                  {m.label}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Job post slots table ─────────────────────────────────────────────────────
const JobPostSlots = ({ jobPostSlots = [] }) => {
  if (!jobPostSlots.length)
    return <p style={{ fontSize: 11, color: C.text4 }}>No slot data.</p>;
  const slotTypes = [
    { key: "booked", label: "Booked", color: C.blue },
    { key: "available", label: "Available", color: C.green },
    { key: "rescheduled", label: "Rescheduled", color: C.amber },
    { key: "blocked", label: "Blocked", color: C.red },
    { key: "inactive", label: "Inactive", color: C.text4 },
  ];
  return (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}
      >
        <thead>
          <tr style={{ borderBottom: `1px solid ${C.border}` }}>
            <th
              style={{
                textAlign: "left",
                padding: "6px 10px 8px",
                fontSize: 10,
                fontWeight: 700,
                color: C.text3,
              }}
            >
              Job post
            </th>
            {slotTypes.map((t) => (
              <th
                key={t.key}
                style={{
                  textAlign: "center",
                  padding: "6px 8px 8px",
                  fontSize: 10,
                  fontWeight: 700,
                  color: C.text3,
                }}
              >
                {t.label}
              </th>
            ))}
            <th
              style={{
                textAlign: "center",
                padding: "6px 8px 8px",
                fontSize: 10,
                fontWeight: 700,
                color: C.text3,
              }}
            >
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {jobPostSlots.map((row) => (
            <tr
              key={row.jobPostId}
              style={{ borderBottom: `1px solid ${C.border}` }}
            >
              <td
                style={{ padding: "8px 10px", fontWeight: 600, color: C.text1 }}
              >
                {row.jobPostName}
              </td>
              {slotTypes.map((t) => (
                <td key={t.key} style={{ padding: "8px", textAlign: "center" }}>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: row[t.key] > 0 ? t.color : C.text4,
                      fontFamily: mono,
                    }}
                  >
                    {row[t.key] ?? 0}
                  </span>
                </td>
              ))}
              <td style={{ padding: "8px", textAlign: "center" }}>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: C.text1,
                    fontFamily: mono,
                  }}
                >
                  {row.total ?? 0}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Error card ───────────────────────────────────────────────────────────────
const ErrorCard = ({ message }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 320,
    }}
  >
    <Card style={{ textAlign: "center", maxWidth: 400, padding: 32 }}>
      <div style={{ fontSize: 28, marginBottom: 10 }}>⚠️</div>
      <p
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: C.text1,
          marginBottom: 6,
        }}
      >
        Failed to load dashboard
      </p>
      <p style={{ fontSize: 12, color: C.text3 }}>{message}</p>
    </Card>
  </div>
);

// ─── Upgrade wall ─────────────────────────────────────────────────────────────
// Shown when the user has the role but the dashboard module is not activated
const UpgradeWall = ({ workspace }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "70vh",
      fontFamily: font,
    }}
  >
    <div
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 14,
        padding: 40,
        maxWidth: 420,
        textAlign: "center",
        boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
      }}
    >
      <div style={{ fontSize: 40, marginBottom: 14 }}>🔒</div>
      <h2
        style={{
          fontSize: 18,
          fontWeight: 700,
          color: C.text1,
          margin: "0 0 8px",
          fontFamily: font,
        }}
      >
        Module not activated
      </h2>
      <p
        style={{
          fontSize: 13,
          color: C.text3,
          margin: "0 0 24px",
          lineHeight: 1.6,
          fontFamily: font,
        }}
      >
        Your plan doesn't include the{" "}
        <strong>
          {workspace === "consultancy" ? "Consultancy" : "Recruitment"}
        </strong>{" "}
        dashboard. Upgrade your plan to unlock full access.
      </p>
      <a
        href="https://ourdept.com"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: "inline-block",
          background: C.blue,
          color: "#fff",
          fontSize: 13,
          fontWeight: 600,
          padding: "10px 24px",
          borderRadius: 8,
          textDecoration: "none",
          fontFamily: font,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
      >
        Upgrade on OurDept →
      </a>
    </div>
  </div>
);

// ══════════════════════════════════════════════════════════════════════════════
// CONSULTANCY HELPERS
// ══════════════════════════════════════════════════════════════════════════════
const ConnectionBars = ({ items = [], color, empty }) => {
  if (!items.length)
    return (
      <p style={{ fontSize: 11, color: C.text4 }}>{empty || "No data."}</p>
    );
  const max = Math.max(...items.map((i) => i.count || 0), 1);
  return (
    <div>
      {items.map((item) => (
        <div key={item.connectionId} style={{ marginBottom: 8 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 3,
            }}
          >
            <span style={{ fontSize: 11, color: C.text2 }}>
              Connection #{item.connectionId}
            </span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: C.text1,
                fontFamily: mono,
              }}
            >
              {item.count}
            </span>
          </div>
          <div style={{ background: C.bg, borderRadius: 99, height: 5 }}>
            <div
              style={{
                width: `${Math.round((item.count / max) * 100)}%`,
                height: "100%",
                background: color,
                borderRadius: 99,
                transition: "width .5s ease",
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const JobPostStatusTable = ({ jobPostStatusDetails = [] }) => {
  const [q, setQ] = useState("");
  const filtered = jobPostStatusDetails.filter(
    (j) =>
      String(j.jobPostId).includes(q) ||
      (j.status || "").toLowerCase().includes(q.toLowerCase()) ||
      (j.jobPostName || "").toLowerCase().includes(q.toLowerCase()),
  );
  return (
    <div>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by name or status…"
        style={{
          width: "100%",
          padding: "7px 10px",
          fontSize: 12,
          border: `1px solid ${C.border}`,
          borderRadius: 7,
          outline: "none",
          fontFamily: font,
          color: C.text1,
          background: C.bg,
          marginBottom: 10,
          boxSizing: "border-box",
        }}
      />
      {!filtered.length ? (
        <p style={{ fontSize: 11, color: C.text4 }}>No job posts found.</p>
      ) : (
        <table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}
        >
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              <th
                style={{
                  textAlign: "left",
                  padding: "6px 10px 8px",
                  fontSize: 10,
                  fontWeight: 700,
                  color: C.text3,
                }}
              >
                Job post
              </th>
              <th
                style={{
                  textAlign: "center",
                  padding: "6px 10px 8px",
                  fontSize: 10,
                  fontWeight: 700,
                  color: C.text3,
                }}
              >
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr
                key={row.jobPostId}
                style={{ borderBottom: `1px solid ${C.border}` }}
              >
                <td style={{ padding: "8px 10px" }}>
                  <span
                    style={{ fontSize: 12, fontWeight: 600, color: C.text1 }}
                  >
                    {row.jobPostName || `Job post #${row.jobPostId}`}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      color: C.text4,
                      marginLeft: 6,
                      fontFamily: mono,
                    }}
                  >
                    #{row.jobPostId}
                  </span>
                </td>
                <td style={{ padding: "8px 10px", textAlign: "center" }}>
                  <Badge status={row.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const CardIcon = ({ icon, bg, color }) => (
  <div
    style={{
      width: 34,
      height: 34,
      borderRadius: 8,
      background: bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    <span style={{ fontSize: 18, color }}>{icon}</span>
  </div>
);

const OpenBadge = ({ count, color, bg }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      background: bg,
      color,
      borderRadius: 99,
      padding: "2px 8px",
      fontSize: 11,
      fontWeight: 600,
    }}
  >
    <span
      style={{
        width: 5,
        height: 5,
        borderRadius: "50%",
        background: color,
        display: "inline-block",
      }}
    />
    {count} open
  </span>
);

const StatusBarRow = ({ label, value, max, color }) => {
  const pct = max ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ marginBottom: 7 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 3,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: color,
              flexShrink: 0,
              display: "inline-block",
            }}
          />
          <span style={{ fontSize: 11, color: C.text2, fontFamily: font }}>
            {label}
          </span>
        </div>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: C.text1,
            fontFamily: mono,
          }}
        >
          {value ?? 0}
        </span>
      </div>
      <div
        style={{
          height: 4,
          background: C.bg,
          borderRadius: 99,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: color,
            borderRadius: 99,
            transition: "width .5s ease",
          }}
        />
      </div>
    </div>
  );
};

const ApprovalBlock = ({ role, pending, approved, rejected }) => (
  <div style={{ marginBottom: 7 }}>
    <p
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: C.text4,
        letterSpacing: "0.05em",
        margin: "0 0 4px",
        fontFamily: font,
      }}
    >
      {role}
    </p>
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {[
        { label: "Approved", value: approved, color: C.green },
        { label: "Pending", value: pending, color: C.amber },
        { label: "Rejected", value: rejected, color: C.red },
      ].map(({ label, value, color }) => (
        <div
          key={label}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span style={{ fontSize: 11, color: C.text3, fontFamily: font }}>
            {label}
          </span>
          <span
            style={{ fontSize: 12, fontWeight: 700, color, fontFamily: mono }}
          >
            {value ?? 0}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const CandidatePill = ({ label, value, color }) => (
  <div
    style={{
      background: C.surface2,
      borderRadius: 8,
      padding: "6px 8px",
      display: "flex",
      alignItems: "center",
      gap: 7,
    }}
  >
    <span
      style={{
        width: 7,
        height: 7,
        borderRadius: 2,
        background: color,
        flexShrink: 0,
        display: "inline-block",
      }}
    />
    <div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          fontFamily: mono,
          color,
          lineHeight: 1,
        }}
      >
        {value ?? 0}
      </div>
      <div
        style={{ fontSize: 10, color: C.text3, marginTop: 1, fontFamily: font }}
      >
        {label}
      </div>
    </div>
  </div>
);

const MiniLabel = ({ children }) => (
  <p
    style={{
      fontSize: 10,
      fontWeight: 700,
      color: C.text4,
      letterSpacing: "0.06em",
      margin: "0 0 6px",
      fontFamily: font,
    }}
  >
    {children}
  </p>
);

const VLine = () => (
  <div
    style={{
      width: 1,
      background: C.border,
      alignSelf: "stretch",
      margin: "0 12px",
      flexShrink: 0,
    }}
  />
);

// ─── VACANCIES CARD ───────────────────────────────────────────────────────────
const VacanciesCard = ({ vacancies }) => {
  const total = vacancies?.total ?? 0;
  const pending = vacancies?.status?.pending ?? 0;
  const approved = vacancies?.status?.approved ?? 0;
  const rejected = vacancies?.status?.rejected ?? 0;
  return (
    <div
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        padding: 12,
        fontFamily: font,
        animation: "dashFadeUp 0.4s ease both",
        animationDelay: "100ms",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start" }}>
        <div style={{ flex: 3 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <CardIcon icon="🏢" bg={C.blueLt} color={C.blue} />
            <div>
              <p
                style={{
                  fontSize: 12,
                  color: C.text3,
                  margin: "0 0 1px",
                  fontFamily: font,
                }}
              >
                Vacancies
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    fontSize: 26,
                    fontWeight: 700,
                    color: C.text1,
                    fontFamily: mono,
                    lineHeight: 1,
                  }}
                >
                  {total}
                </span>
                <OpenBadge count={approved} color={C.green} bg={C.greenLt} />
              </div>
            </div>
          </div>
          <MiniLabel>Status</MiniLabel>
          <StatusBarRow
            label="Pending"
            value={pending}
            max={total}
            color={C.amber}
          />
          <StatusBarRow
            label="Approved"
            value={approved}
            max={total}
            color={C.green}
          />
          <StatusBarRow
            label="Rejected"
            value={rejected}
            max={total}
            color={C.red}
          />
        </div>
        <VLine />
        <div style={{ flex: 1, minWidth: 0 }}>
          <ApprovalBlock
            role="HOD"
            pending={vacancies?.hod?.pending}
            approved={vacancies?.hod?.approved}
            rejected={vacancies?.hod?.rejected}
          />
          <div style={{ height: 1, background: C.border, margin: "5px 0" }} />
          <ApprovalBlock
            role="HR"
            pending={vacancies?.hr?.pending}
            approved={vacancies?.hr?.approved}
            rejected={vacancies?.hr?.rejected}
          />
        </div>
      </div>
    </div>
  );
};

// ─── JOB POSTS CARD ───────────────────────────────────────────────────────────
const JobPostsCard = ({ jobPosts, candidates }) => {
  const total = jobPosts?.total ?? 0;
  const pending = jobPosts?.status?.pending ?? 0;
  const approved = jobPosts?.status?.approved ?? 0;
  const rejected = jobPosts?.status?.rejected ?? 0;

  return (
    <div
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        padding: 12,
        fontFamily: font,
        animation: "dashFadeUp 0.4s ease both",
        animationDelay: "120ms",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start" }}>
        <div style={{ flex: 3 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <CardIcon icon="📋" bg={C.violetLt} color={C.violet} />
            <div>
              <p
                style={{
                  fontSize: 12,
                  color: C.text3,
                  margin: "0 0 1px",
                  fontFamily: font,
                }}
              >
                Job posts
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    fontSize: 26,
                    fontWeight: 700,
                    color: C.text1,
                    fontFamily: mono,
                    lineHeight: 1,
                  }}
                >
                  {total}
                </span>
                <OpenBadge count={approved} color={C.green} bg={C.greenLt} />
              </div>
            </div>
          </div>
          <MiniLabel>Status</MiniLabel>
          <StatusBarRow
            label="Pending"
            value={pending}
            max={total}
            color={C.amber}
          />
          <StatusBarRow
            label="Approved"
            value={approved}
            max={total}
            color={C.green}
          />
          <StatusBarRow
            label="Rejected"
            value={rejected}
            max={total}
            color={C.red}
          />
        </div>
        <VLine />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <CandidatePill
              label="Processing"
              value={candidates?.processing}
              color={C.blue}
            />
            <CandidatePill
              label="On hold"
              value={candidates?.hold}
              color={C.amber}
            />
            <CandidatePill
              label="Selected"
              value={candidates?.selected}
              color={C.green}
            />
            <CandidatePill
              label="Rejected"
              value={candidates?.rejected}
              color={C.red}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// CONSULTANCY DASHBOARD
// ══════════════════════════════════════════════════════════════════════════════
function ConsultancyView({ isEmployee = false }) {
  const { canAccessModuleFeature } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    setLoading(true);
    axiosInstance
      .get(isEmployee ? APIS.empConsultancy : APIS.consultancy)
      .then((res) => setData(res.data.data))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [isEmployee]);

  // ── Module guard — must have CONSULTANCY.DASHBOARD module code ──
  if (!canAccessModuleFeature(CONSULTANCY.DASHBOARD)) {
    return <UpgradeWall workspace="consultancy" />;
  }

  if (loading) return <LoadingScreen />;
  if (error) return <ErrorCard message={error} />;
  if (!data) return null;

  const {
    totalCandidateRequirements,
    candidateRequirementByConnection,
    totalSharedCandidates,
    sharedCandidatesByConnection,
    jobPostStatusDetails,
  } = data;
  const rate = totalCandidateRequirements
    ? Math.round((totalSharedCandidates / totalCandidateRequirements) * 100)
    : 0;
  const totalPosts = (jobPostStatusDetails || []).length;
  const openPosts = (jobPostStatusDetails || []).filter((j) =>
    ["open", "active"].includes((j.status || "").toLowerCase()),
  ).length;
  const connections = new Set([
    ...(candidateRequirementByConnection || []).map((c) => c.connectionId),
    ...(sharedCandidatesByConnection || []).map((c) => c.connectionId),
  ]).size;

  const donutData = [
    {
      label: "Shared",
      count: Math.min(
        totalSharedCandidates || 0,
        totalCandidateRequirements || 0,
      ),
      pct: rate,
      color: C.green,
    },
    {
      label: "Remaining",
      count: Math.max(
        (totalCandidateRequirements || 0) - (totalSharedCandidates || 0),
        0,
      ),
      pct: 100 - rate,
      color: C.border,
    },
  ].filter((d) => d.count > 0);

  return (
    <>
      <div style={{ marginBottom: 10, animation: "dashFadeUp .3s ease both" }}>
        <h1
          style={{ fontSize: 18, fontWeight: 700, color: C.text1, margin: 0 }}
        >
          Consultancy dashboard
        </h1>
        <p style={{ fontSize: 11, color: C.text3, margin: "3px 0 0" }}>
          {new Date().toLocaleDateString("en-IN", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
        </p>
      </div>

      {/* KPI row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4,1fr)",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <StatCard
          delay={0}
          label="Candidate requirements"
          value={totalCandidateRequirements}
          accent={C.blue}
          sub="total across connections"
        />
        <StatCard
          delay={50}
          label="Shared candidates"
          value={totalSharedCandidates}
          accent={C.green}
          sub={`${rate}% fulfillment rate`}
        />
        <StatCard
          delay={100}
          label="Job posts"
          value={totalPosts}
          accent={C.amber}
          sub={`${openPosts} open`}
        />
        <StatCard
          delay={150}
          label="Connections"
          value={connections}
          accent={C.violet}
          sub="active connections"
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 2fr",
          gap: 10,
          marginBottom: 10,
        }}
      >
        <Card
          style={{
            animation: "dashFadeUp .4s ease both",
            animationDelay: "120ms",
          }}
        >
          <SectionLabel right={`${rate}% fulfilled`}>Fulfillment</SectionLabel>
          <DonutChart
            data={donutData}
            centerLabel="fulfilled"
            centerValue={`${rate}%`}
          />
          <Div />
          <DataGrid
            items={[
              {
                label: "Requirements",
                value: totalCandidateRequirements || 0,
                color: C.blue,
              },
              {
                label: "Shared",
                value: totalSharedCandidates || 0,
                color: C.green,
              },
              {
                label: "Gap",
                value: Math.max(
                  (totalCandidateRequirements || 0) -
                    (totalSharedCandidates || 0),
                  0,
                ),
                color: C.text3,
              },
            ]}
          />
        </Card>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Card
            style={{
              animation: "dashFadeUp .4s ease both",
              animationDelay: "140ms",
            }}
          >
            <SectionLabel
              count={(candidateRequirementByConnection || []).length}
            >
              Requirements by connection
            </SectionLabel>
            <ConnectionBars
              items={candidateRequirementByConnection}
              color={C.blue}
              empty="No requirement data."
            />
          </Card>
          <Card
            style={{
              animation: "dashFadeUp .4s ease both",
              animationDelay: "160ms",
            }}
          >
            <SectionLabel count={(sharedCandidatesByConnection || []).length}>
              Shared by connection
            </SectionLabel>
            <ConnectionBars
              items={sharedCandidatesByConnection}
              color={C.green}
              empty="No shared candidate data."
            />
          </Card>
        </div>
      </div>

      <Card
        style={{
          animation: "dashFadeUp .4s ease both",
          animationDelay: "180ms",
        }}
      >
        <SectionLabel count={totalPosts}>Job post status</SectionLabel>
        <JobPostStatusTable jobPostStatusDetails={jobPostStatusDetails} />
      </Card>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// RECRUITMENT DASHBOARD
// ══════════════════════════════════════════════════════════════════════════════
function RecruitmentView({ isEmployee = false }) {
  const { canAccessModuleFeature } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [slots, setSlots] = useState(null);
  const [mySlots, setMySlots] = useState([]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axiosInstance.get(isEmployee ? APIS.empDashboard : APIS.hrDashboard),
      axiosInstance.get(isEmployee ? APIS.empSlots : APIS.hrSlots),
      axiosInstance.get(APIS.mySlots),
    ])
      .then(([d, s, ms]) => {
        setData(d.data.data);
        setSlots(s.data.data);
        setMySlots(ms.data.data || []);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [isEmployee]);

  // ── Module guard — must have RECRUITMENT.DASHBOARD module code ──
  if (!canAccessModuleFeature(RECRUITMENT.DASHBOARD)) {
    return <UpgradeWall workspace="recruitment" />;
  }

  if (loading) return <LoadingScreen />;
  if (error) return <ErrorCard message={error} />;
  if (!data) return null;

  const { vacancies, jobPosts, candidates, jobPostCandidates, appliedVia } =
    data;
  const selRate = candidates.totalApplied
    ? Math.round((candidates.selected / candidates.totalApplied) * 100)
    : 0;
  const overall = slots?.overall || {};
  const apvData = parseAppliedVia(appliedVia);

  return (
    <>
      {/* Header */}
      <div style={{ marginBottom: 10, animation: "dashFadeUp .3s ease both" }}>
        <h1
          style={{ fontSize: 18, fontWeight: 700, color: C.text1, margin: 0 }}
        >
          Recruitment dashboard
        </h1>
        <p style={{ fontSize: 11, color: C.text3, margin: "3px 0 0" }}>
          {new Date().toLocaleDateString("en-IN", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
        </p>
      </div>

      {/* KPI strip */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(6,1fr)",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <StatCard
          delay={0}
          label="Vacancies"
          value={vacancies.total}
          accent={C.green}
          sub={`${vacancies.status.approved ?? 0} approved`}
        />
        <StatCard
          delay={40}
          label="Job posts"
          value={jobPosts.total}
          accent={C.blue}
          sub={`${jobPosts.status.open ?? 0} active`}
        />
        <StatCard
          delay={80}
          label="Applied"
          value={candidates.totalApplied}
          accent="#d85a30"
          sub="across all posts"
        />
        <StatCard
          delay={120}
          label="Selected"
          value={candidates.selected}
          accent={C.green}
          sub={`${selRate}% rate`}
        />
        <StatCard
          delay={160}
          label="Scheduled"
          value={candidates.scheduled}
          accent={C.violet}
          sub="interviews ahead"
        />
        <StatCard
          delay={200}
          label="Total slots"
          value={overall.total ?? 0}
          accent={C.amber}
          sub={`${overall.booked ?? 0} booked`}
        />
      </div>

      {/* Candidate overview */}
      <Card
        style={{
          marginBottom: 10,
          animation: "dashFadeUp .4s ease both",
          animationDelay: "80ms",
        }}
      >
        <SectionLabel right={`${selRate}% selection rate`}>
          Candidate overview
        </SectionLabel>
        <CandidateStrip candidates={candidates} />
      </Card>

      {/* Vacancies + Job posts */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 10,
          marginBottom: 10,
        }}
      >
        <VacanciesCard vacancies={vacancies} />
        <JobPostsCard jobPosts={jobPosts} candidates={candidates} />
      </div>

      {/* Hiring pipeline */}
      <Card
        style={{
          marginBottom: 10,
          animation: "dashFadeUp .4s ease both",
          animationDelay: "140ms",
        }}
      >
        <HiringPipelineCard jobPostCandidates={jobPostCandidates} />
      </Card>

      {/* Sources + interviews + slot availability */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 10,
          marginBottom: 10,
        }}
      >
        <Card
          style={{
            animation: "dashFadeUp .4s ease both",
            animationDelay: "160ms",
          }}
        >
          <SectionLabel>Hiring sources</SectionLabel>
          <DonutChart
            data={apvData}
            centerLabel="total"
            centerValue={candidates.totalApplied}
          />
          <p
            style={{
              fontSize: 10,
              color: C.text4,
              marginTop: 10,
              paddingTop: 10,
              borderTop: `1px solid ${C.border}`,
            }}
          >
            Based on {candidates.totalApplied} applications
          </p>
        </Card>
        <Card
          style={{
            animation: "dashFadeUp .4s ease both",
            animationDelay: "180ms",
          }}
        >
          <SectionLabel>Upcoming interviews</SectionLabel>
          <UpcomingInterviews slots={mySlots} />
        </Card>
        <Card
          style={{
            animation: "dashFadeUp .4s ease both",
            animationDelay: "200ms",
          }}
        >
          <SectionLabel
            right={
              <span
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: C.text1,
                  fontFamily: mono,
                }}
              >
                {overall.total ?? 0}
              </span>
            }
          >
            Slot availability
          </SectionLabel>
          {[
            { key: "booked", label: "Booked", color: C.blue },
            { key: "available", label: "Available", color: C.green },
            { key: "rescheduled", label: "Rescheduled", color: C.amber },
            { key: "blocked", label: "Blocked", color: C.red },
            { key: "inactive", label: "Inactive", color: C.text4 },
          ].map((b) => (
            <BarRow
              key={b.key}
              label={b.label}
              value={overall[b.key] || 0}
              max={overall.total || 1}
              color={b.color}
            />
          ))}
        </Card>
      </div>

      {/* Slots by job post + Calendar */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr",
          gap: 10,
          marginBottom: 10,
        }}
      >
        <Card
          style={{
            animation: "dashFadeUp .4s ease both",
            animationDelay: "220ms",
          }}
        >
          <SectionLabel count={slots?.jobPostSlots?.length}>
            Slots by job post
          </SectionLabel>
          <JobPostSlots jobPostSlots={slots?.jobPostSlots || []} />
        </Card>
        <Card
          style={{
            animation: "dashFadeUp .4s ease both",
            animationDelay: "240ms",
          }}
        >
          <SectionLabel>Interview calendar</SectionLabel>
          <MiniCalendar slots={mySlots} />
        </Card>
      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ROOT
// ══════════════════════════════════════════════════════════════════════════════
const Dashboard = () => {
  const { workspace } = useWorkspace();
  return (
    <>
      <FontLoader />
      <style>{`
        @keyframes dashFadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        *, *::before, *::after { box-sizing: border-box; }
      `}</style>
      <div
        style={{
          minHeight: "100vh",
          fontFamily: font,
          padding: "10px",
          background: C.bg,
        }}
      >
        {workspace === WORKSPACES.CONSULTANT ? (
          <ConsultancyView isEmployee={false} />
        ) : workspace === WORKSPACES.EMPLOYEE ? (
          <RecruitmentView isEmployee={true} />
        ) : (
          <RecruitmentView isEmployee={false} />
        )}
      </div>
    </>
  );
};

export default Dashboard;
